/**
 * ConnectJP Section Scroll Lifecycle Observer
 * Handles 3 lifecycle states as user scrolls through sections:
 * 1. Entering (cj-entering): Entrance slide-in / fade-in / scale-in.
 * 2. Active (cj-active): Continuous subtle ambient motion (floating cards, glowing elements, image breathing).
 * 3. Exiting (cj-exiting): Exit fade-out / scale-down when section leaves viewport.
 *
 * Also provides scroll-driven horizontal gallery fallback for browsers
 * that don't support CSS animation-timeline: view().
 */

(function () {
  'use strict';

  function initConnectJPLifecycle() {
    // Auto-discover all homepage & theme sections
    const sections = document.querySelectorAll('section, [class*="connectjp-"], [id*="connectjp-"]');
    if (!sections.length) return;

    // Mark all sections with storytelling lifecycle attribute
    sections.forEach((sec) => {
      sec.setAttribute('data-cj-lifecycle', 'true');
    });

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      sections.forEach((section) => {
        section.classList.add('cj-active');
        section.classList.remove('cj-entering', 'cj-exiting');
      });
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -15% 0px', // Triggers storytelling reveal 15% inside viewport
      threshold: 0.08
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target;

        if (entry.isIntersecting) {
          if (!target.classList.contains('cj-active') && !target.classList.contains('cj-entering')) {
            target.classList.remove('cj-exiting');
            target.classList.add('cj-entering');

            // Switch to active state after entrance animation completes
            setTimeout(() => {
              target.classList.remove('cj-entering');
              target.classList.add('cj-active');
            }, 850);
          }
        } else {
          // Reset when scrolled far outside viewport
          const rect = entry.boundingClientRect;
          if (rect.top > window.innerHeight || rect.bottom < 0) {
            target.classList.remove('cj-entering', 'cj-active');
            target.classList.add('cj-exiting');
          }
        }
      });
    }, observerOptions);

    sections.forEach((section) => {
      sectionObserver.observe(section);
    });
  }

  /**
   * Horizontal Gallery Scroll Fallback
   * For browsers that don't support CSS animation-timeline: view()
   * (e.g. Firefox). Uses scroll event listener to calculate
   * horizontal translation based on the gallery's viewport progress.
   */
  function initGalleryFallback() {
    // Check if native scroll-driven animations are supported
    if (CSS.supports('(animation-timeline: view()) and (animation-range: entry)')) {
      return; // Native CSS handles it — no fallback needed
    }

    const gallery = document.getElementById('cjApproachGallery');
    if (!gallery || gallery.hasAttribute('data-cj-carousel')) return;

    const sticky = gallery.querySelector('.cj-gallery-sticky');
    const strip = gallery.querySelector('.cj-gallery-strip');
    if (!sticky || !strip) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    // Set up the fallback layout: stretch the gallery for scroll estate
    gallery.style.height = '500vh';
    gallery.style.overflow = 'visible';

    sticky.style.height = '100vh';
    sticky.style.width = '100%';
    sticky.style.maxWidth = '100%';
    sticky.style.position = 'sticky';
    sticky.style.top = '0';
    sticky.style.overflow = 'hidden';

    strip.style.height = '100vh';
    strip.style.width = 'max-content';

    let ticking = false;

    function updateGalleryTransform() {
      const galleryRect = gallery.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate scroll progress through the gallery section
      // The animation should play while the gallery "contains" the viewport
      // i.e., gallery top <= 0 and gallery bottom >= viewportHeight
      const galleryTop = galleryRect.top;
      const galleryHeight = gallery.offsetHeight;

      // Total scrollable range within the gallery
      const scrollRange = galleryHeight - viewportHeight;

      if (scrollRange <= 0) return;

      // How far we've scrolled into the gallery (0 = top just hit viewport top, scrollRange = bottom just hit viewport bottom)
      const scrolled = -galleryTop;
      const progress = Math.max(0, Math.min(1, scrolled / scrollRange));

      // Calculate the strip translation
      const stripWidth = strip.scrollWidth;
      const maxTranslate = stripWidth - sticky.clientWidth;

      const translateX = -progress * maxTranslate;

      strip.style.transform = `translateX(${translateX}px)`;

      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateGalleryTransform);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    updateGalleryTransform();

    // Recalculate on resize
    window.addEventListener('resize', () => {
      requestAnimationFrame(updateGalleryTransform);
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initConnectJPLifecycle();
      initGalleryFallback();
    });
  } else {
    initConnectJPLifecycle();
    initGalleryFallback();
  }

  // Support Shopify Theme Editor events
  document.addEventListener('shopify:section:load', () => {
    initConnectJPLifecycle();
    initGalleryFallback();
  });
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target) {
      e.target.classList.add('cj-active');
      e.target.classList.remove('cj-entering', 'cj-exiting');
    }
  });
  // Handle browser back button (bfcache restoration)
  window.addEventListener('pageshow', (e) => {
    // If returning via Back button (bfcache or history traversal)
    if (e.persisted || performance.getEntriesByType("navigation")[0]?.type === "back_forward") {
      const isHome = document.querySelector('main[data-template="index"]') || window.location.pathname === '/' || document.querySelector('.connectjp-hero');

      if (isHome) {
        // Reset scroll position to top instantly when returning to homepage
        window.scrollTo({ top: 0, behavior: 'instant' });
        const container = document.querySelector('.page-wrapper') || document.scrollingElement;
        if (container) container.scrollTop = 0;
      }

      // Reset lifecycle states so the UI starts cleanly
      document.querySelectorAll('[data-cj-lifecycle]').forEach((sec, idx) => {
        sec.classList.remove('cj-exiting', 'cj-entering');
        if (isHome && idx > 0) {
          sec.classList.remove('cj-active');
        } else {
          sec.classList.add('cj-active');
        }
      });
    }
  });

  // Global Delegated Hash & About Button Smooth Scroll Handler
  document.addEventListener('click', function (e) {
    const anchor = e.target.closest('a.esim-btn-secondary, a.cj-scroll-about, a[href*="#esim-about"], a[href*="#about-us"], a[href*="#about"], [data-scroll-target]');
    if (!anchor) return;

    const rawHref = anchor.getAttribute('data-scroll-target') || anchor.getAttribute('href') || '#esim-about';
    let hash = '#esim-about';
    if (rawHref && rawHref.includes('#')) {
      const parts = rawHref.split('#');
      if (parts[1] && parts[1].trim() !== '') {
        hash = '#' + parts[1].trim();
      }
    }

    const target =
      (hash && hash.length > 1 ? document.querySelector(hash) : null) ||
      document.getElementById(hash.replace('#', '')) ||
      document.getElementById('esim-about') ||
      document.getElementById('about-us') ||
      document.querySelector('.esim-about-section') ||
      document.querySelector('.home-abouts-section') ||
      document.querySelector('[id*="about"]');

    if (target) {
      e.preventDefault();

      // 1. Update URL hash in browser address bar (User requirement: "hiển link lên url")
      try {
        if (window.history && window.history.pushState) {
          window.history.pushState(null, null, hash);
        } else {
          window.location.hash = hash;
        }
      } catch (err) {
        window.location.hash = hash;
      }

      // 2. Perform Smooth Scroll
      const headerOffset = 90;
      const targetY = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: 'smooth'
      });

      // 3. Fallback scrollIntoView to guarantee scroll happens
      setTimeout(function () {
        const currentDist = Math.abs(target.getBoundingClientRect().top - headerOffset);
        if (currentDist > 40) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, true);
})();
